import React from 'react';
import {
  Edit,
  Eye,
  FileText,
  Headphones,
  Image,
  Link,
  Loader2,
  Plus,
  Trash2,
  Upload,
  Video,
  X,
} from 'lucide-react';
import { Card } from '../../ui/card';
import { Button } from '../../ui/button';
import { Badge } from '../../ui/badge';
import { Input } from '../../ui/input';
import { Textarea } from '../../ui/textarea';

const resolveDocumentUrl = (url, updatedAt) => {
  if (!url || typeof url !== 'string') return url;
  const versionedUrl = updatedAt
    ? `${url}${url.includes('?') ? '&' : '?'}v=${encodeURIComponent(updatedAt)}`
    : url;
  return versionedUrl;
};

const getDocumentViewMeta = (doc) => {
  const resolvedUrl = resolveDocumentUrl(doc?.file_url || doc?.content, doc?.updated_at);
  const hasViewableSource = Boolean(
    doc?.file_url || (doc?.content_type !== 'text' && doc?.content && !doc.content.startsWith('file://'))
  );
  const isMissingInternalFile = Boolean(doc?.file_url && doc?.file_missing);

  return {
    resolvedUrl,
    hasViewableSource,
    isMissingInternalFile,
  };
};

const DocumentsTab = ({
  adminFetch,
  documents,
  availableTags,
  categoryFilter,
  setCategoryFilter,
  loading,
  showAddModal,
  setShowAddModal,
  showEditModal,
  setShowEditModal,
  selectedDoc,
  setSelectedDoc,
  saving,
  searchTerm,
  setSearchTerm,
  selectedFile,
  setSelectedFile,
  dragOver,
  setDragOver,
  newTag,
  setNewTag,
  uploadProgress,
  fileInputRef,
  formData,
  setFormData,
  resetForm,
  handleFileSelect,
  handleDrop,
  toggleTag,
  addCustomTag,
  handleAddDocument,
  handleEditDocument,
  handleDeleteDocument,
  openEditModal,
}) => {
  const categoryLabels = {
    guest_guide: { label: 'Guide Partner', color: 'bg-blue-500/10 text-blue-400 border-blue-500/30' },
    onboarding: { label: 'Onboarding', color: 'bg-green-500/10 text-green-400 border-green-500/30' },
    member: { label: 'Member', color: 'bg-aqua/10 text-aqua border-aqua/30' },
    general: { label: 'General', color: 'bg-gray-500/10 text-gray-400 border-gray-500/30' }
  };

  const contentTypeLabels = {
    link: { label: 'Link', icon: Link },
    pdf: { label: 'PDF', icon: FileText },
    doc: { label: 'Doc', icon: FileText },
    video: { label: 'Video', icon: Video },
    audio: { label: 'Audio', icon: Headphones },
    image: { label: 'Image', icon: Image },
    other: { label: 'Other', icon: FileText },
  };

  const filteredDocs = documents.filter(d =>
    d.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    d.description?.toLowerCase().includes(searchTerm.toLowerCase())
  );
  const selectedDocView = getDocumentViewMeta(selectedDoc);
  const openDocument = async (doc) => {
    const url = getDocumentViewMeta(doc).resolvedUrl;
    if (!url?.startsWith('/api/documents/file/')) {
      window.open(url, '_blank', 'noopener,noreferrer');
      return;
    }
    const preview = window.open('', '_blank');
    if (!preview) return;
    try {
      const response = await adminFetch(url);
      if (!response.ok) throw new Error('Unable to open document');
      const objectUrl = URL.createObjectURL(await response.blob());
      preview.location.href = objectUrl;
      setTimeout(() => URL.revokeObjectURL(objectUrl), 60000);
    } catch {
      preview.close();
      window.alert('Unable to open this document. Please try again.');
    }
  };


  const addFormJSX = (
    <div className="space-y-4">
      <div>
        <label className="text-sm text-gray-400 mb-2 block">Upload Document (max 25MB)</label>
        <input
          type="file"
          ref={fileInputRef}
          onChange={handleFileSelect}
          accept=".pdf,.doc,.docx"
          className="hidden"
        />
        <div
          onClick={() => fileInputRef.current?.click()}
          onDrop={handleDrop}
          onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
          onDragLeave={(e) => { e.preventDefault(); setDragOver(false); }}
          className={`border-2 border-dashed rounded-xl p-6 text-center cursor-pointer transition-colors ${
            dragOver ? 'border-aqua bg-aqua/5' : 'border-[#2a2a32] hover:border-aqua/30'
          }`}
        >
          {selectedFile ? (
            <div className="flex items-center justify-center gap-3">
              <FileText className="w-8 h-8 text-aqua" />
              <div className="text-left">
                <p className="text-white font-medium">{selectedFile.name}</p>
                <p className="text-gray-500 text-sm">{(selectedFile.size / 1024 / 1024).toFixed(2)} MB</p>
              </div>
              <button
                onClick={(e) => { e.stopPropagation(); setSelectedFile(null); }}
                className="ml-2 p-1 text-gray-400 hover:text-red-400"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          ) : (
            <>
              <Upload className="w-10 h-10 text-gray-500 mx-auto mb-2" />
              <p className="text-white mb-1">Drop a document here or click to browse</p>
              <p className="text-gray-500 text-sm">PDF or Word document (max 25MB)</p>
            </>
          )}
        </div>
        {uploadProgress > 0 && (
          <div className="mt-2 w-full bg-[#16161a] rounded-full h-2">
            <div className="bg-aqua h-2 rounded-full transition-all" style={{ width: `${uploadProgress}%` }}></div>
          </div>
        )}
      </div>

      <div>
        <label className="text-sm text-gray-400 mb-1 block">Document Name *</label>
        <Input
          value={formData.name}
          onChange={(e) => setFormData({ ...formData, name: e.target.value })}
          className="bg-charcoal border-charcoal-lighter text-white"
          placeholder="e.g., Community Guidelines"
        />
      </div>
      <div>
        <label className="text-sm text-gray-400 mb-1 block">Description</label>
        <Textarea
          value={formData.description}
          onChange={(e) => setFormData({ ...formData, description: e.target.value })}
          className="bg-charcoal border-charcoal-lighter text-white"
          placeholder="Brief description of the document"
          rows={2}
        />
      </div>
      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="text-sm text-gray-400 mb-1 block">Category</label>
          <select
            value={formData.category}
            onChange={(e) => setFormData({ ...formData, category: e.target.value })}
            className="w-full bg-charcoal border border-charcoal-lighter text-white rounded-lg px-3 py-2"
          >
            <option value="general">General</option>
            <option value="guest_guide">Guide Partner Materials</option>
            <option value="onboarding">Onboarding</option>
            <option value="member">Member Resources</option>
          </select>
        </div>
        <div>
          <label className="text-sm text-gray-400 mb-1 block">Access Level</label>
          <select
            value={formData.access_level}
            onChange={(e) => setFormData({ ...formData, access_level: e.target.value, is_public: e.target.value === 'public' })}
            className="w-full bg-charcoal border border-charcoal-lighter text-white rounded-lg px-3 py-2"
          >
            <option value="public">Public (visible in Help section)</option>
            <option value="members">Signed-in Users</option>
            <option value="admin">Admin Only</option>
          </select>
        </div>
      </div>

      <div>
        <label className="text-sm text-gray-400 mb-2 block">Tags</label>
        <div className="flex flex-wrap gap-2 mb-2">
          {availableTags.map(tag => (
            <button
              key={tag}
              type="button"
              onClick={() => toggleTag(tag)}
              className={`px-3 py-1 rounded-full text-sm transition-colors ${
                formData.tags.includes(tag)
                  ? 'bg-aqua text-charcoal'
                  : 'bg-[#2a2a32] text-gray-400 hover:bg-[#3a3a42]'
              }`}
            >
              {tag.replace(/_/g, ' ')}
            </button>
          ))}
        </div>
        <div className="flex gap-2">
          <Input
            value={newTag}
            onChange={(e) => setNewTag(e.target.value)}
            placeholder="Add custom tag..."
            className="bg-charcoal border-charcoal-lighter text-white text-sm"
            onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addCustomTag())}
          />
          <Button type="button" onClick={addCustomTag} size="sm" variant="outline" className="border-charcoal-lighter text-gray-400">
            <Plus className="w-4 h-4" />
          </Button>
        </div>
      </div>
    </div>
  );

  const editFormJSX = (
    <div className="space-y-4">
      <div>
        <label className="text-sm text-gray-400 mb-2 block">Current Document Source</label>
        <div className="rounded-xl border border-[#2a2a32] bg-charcoal p-4">
          {selectedDoc?.file_url ? (
            <div className="flex items-center justify-between gap-3">
              <div>
                <p className="text-white font-medium">{selectedDoc?.file_name || 'Stored document file'}</p>
                <p className="text-gray-500 text-sm">
                  {selectedDocView.isMissingInternalFile
                    ? 'This document record still exists, but the stored file is missing from server storage.'
                    : 'This document is currently stored inside Dad Partners.'}
                </p>
              </div>
              {selectedDocView.isMissingInternalFile ? (
                <span className="inline-flex items-center gap-2 rounded-lg border border-red-500/30 bg-red-500/10 px-3 py-2 text-sm text-red-300">
                  <Eye className="w-4 h-4" />
                  Re-upload needed
                </span>
              ) : (
                <button type="button"
                  onClick={() => openDocument(selectedDoc)}
                  className="inline-flex items-center gap-2 rounded-lg border border-[#2a2a32] px-3 py-2 text-sm text-gray-300 hover:text-aqua"
                >
                  <Eye className="w-4 h-4" />
                  View
                </button>
              )}
            </div>
          ) : selectedDoc?.content ? (
            <div className="flex items-center justify-between gap-3">
              <div>
                <p className="text-white font-medium">External link or inline content</p>
                <p className="text-gray-500 text-sm break-all">
                  {selectedDoc.content}
                </p>
              </div>
              {typeof selectedDoc.content === 'string' && /^https?:\/\//i.test(selectedDoc.content) ? (
                <a
                  href={selectedDoc.content}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 rounded-lg border border-[#2a2a32] px-3 py-2 text-sm text-gray-300 hover:text-aqua"
                >
                  <Eye className="w-4 h-4" />
                  Open
                </a>
              ) : null}
            </div>
          ) : (
            <p className="text-sm text-gray-400">No file or external link is attached yet.</p>
          )}
        </div>
      </div>

      <div>
        <label className="text-sm text-gray-400 mb-2 block">Replace File</label>
        <p className="text-xs text-gray-500 mb-3">
          Upload a new PDF or Word file here to replace the current document while keeping this same document record.
        </p>
        <input
          type="file"
          ref={fileInputRef}
          onChange={handleFileSelect}
          accept=".pdf,.doc,.docx"
          className="hidden"
        />
        <div
          onClick={() => fileInputRef.current?.click()}
          onDrop={handleDrop}
          onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
          onDragLeave={(e) => { e.preventDefault(); setDragOver(false); }}
          className={`border-2 border-dashed rounded-xl p-5 text-center cursor-pointer transition-colors ${
            dragOver ? 'border-aqua bg-aqua/5' : 'border-[#2a2a32] hover:border-aqua/30'
          }`}
        >
          {selectedFile ? (
            <div className="flex items-center justify-center gap-3">
              <FileText className="w-8 h-8 text-aqua" />
              <div className="text-left">
                <p className="text-white font-medium">{selectedFile.name}</p>
                <p className="text-gray-500 text-sm">{(selectedFile.size / 1024 / 1024).toFixed(2)} MB</p>
              </div>
              <button
                onClick={(e) => { e.stopPropagation(); setSelectedFile(null); }}
                className="ml-2 p-1 text-gray-400 hover:text-red-400"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          ) : (
            <>
              <Upload className="w-10 h-10 text-gray-500 mx-auto mb-2" />
              <p className="text-white mb-1">Drop a replacement file here or click to browse</p>
              <p className="text-gray-500 text-sm">
                If this document currently uses a link, uploading here will convert it into a file-based document.
              </p>
            </>
          )}
        </div>
        {uploadProgress > 0 ? (
          <div className="mt-2 w-full bg-[#16161a] rounded-full h-2">
            <div className="bg-aqua h-2 rounded-full transition-all" style={{ width: `${uploadProgress}%` }}></div>
          </div>
        ) : null}
      </div>

      <div>
        <label className="text-sm text-gray-400 mb-1 block">Document Name *</label>
        <Input
          value={formData.name}
          onChange={(e) => setFormData({ ...formData, name: e.target.value })}
          className="bg-charcoal border-charcoal-lighter text-white"
          placeholder="e.g., Community Guidelines"
        />
      </div>
      <div>
        <label className="text-sm text-gray-400 mb-1 block">Description</label>
        <Textarea
          value={formData.description}
          onChange={(e) => setFormData({ ...formData, description: e.target.value })}
          className="bg-charcoal border-charcoal-lighter text-white"
          placeholder="Brief description of the document"
          rows={2}
        />
      </div>
      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="text-sm text-gray-400 mb-1 block">Category</label>
          <select
            value={formData.category}
            onChange={(e) => setFormData({ ...formData, category: e.target.value })}
            className="w-full bg-charcoal border border-charcoal-lighter text-white rounded-lg px-3 py-2"
          >
            <option value="general">General</option>
            <option value="guest_guide">Guide Partner Materials</option>
            <option value="onboarding">Onboarding</option>
            <option value="member">Member Resources</option>
          </select>
        </div>
        <div>
          <label className="text-sm text-gray-400 mb-1 block">Access Level</label>
          <select
            value={formData.access_level}
            onChange={(e) => setFormData({ ...formData, access_level: e.target.value, is_public: e.target.value === 'public' })}
            className="w-full bg-charcoal border border-charcoal-lighter text-white rounded-lg px-3 py-2"
          >
            <option value="public">Public</option>
            <option value="members">Signed-in Users</option>
            <option value="admin">Admin Only</option>
          </select>
        </div>
      </div>
      <div>
        <label className="text-sm text-gray-400 mb-2 block">Tags</label>
        <div className="flex flex-wrap gap-2">
          {availableTags.map(tag => (
            <button
              key={tag}
              type="button"
              onClick={() => toggleTag(tag)}
              className={`px-3 py-1 rounded-full text-sm transition-colors ${
                formData.tags.includes(tag)
                  ? 'bg-aqua text-charcoal'
                  : 'bg-[#2a2a32] text-gray-400 hover:bg-[#3a3a42]'
              }`}
            >
              {tag.replace(/_/g, ' ')}
            </button>
          ))}
        </div>
      </div>
    </div>
  );

  return (
    <div className="space-y-6">
      <Card className="border-slate-200 bg-gradient-to-br from-[#f5f8fb] via-white to-[#eef2f6] p-6 dark:border-white/10 dark:bg-gradient-to-br dark:from-[#20232a] dark:via-[#171b20] dark:to-[#11161a]">
        <p className="text-[11px] font-semibold uppercase tracking-[0.26em] text-slate-600 dark:text-gray-400">
          Admin Systems flow
        </p>
        <h3 className="mt-3 text-2xl font-semibold text-slate-900 dark:text-white">
          Keep the operating documents easy to trust under pressure
        </h3>
        <p className="mt-3 text-sm leading-6 text-slate-600 dark:text-gray-300">
          This is the quiet document layer that supports the operation: onboarding files, guide materials, member resources, and the admin library people need when work is moving fast.
        </p>
      </Card>

      <Card className="bg-[#1e1e24] border-[#2a2a32] p-6">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div>
            <h3 className="text-lg font-semibold text-white flex items-center gap-2">
              <FileText className="w-5 h-5 text-aqua" />
              Document Library
            </h3>
            <p className="text-gray-400 text-sm mt-1">
              Upload and manage PDFs, guides, and resources. Max file size: 25MB
            </p>
          </div>
          <Button
            onClick={() => { resetForm(); setShowAddModal(true); }}
            className="bg-aqua hover:bg-aqua-dark text-charcoal gap-2"
          >
            <Plus className="w-4 h-4" />
            Add Document
          </Button>
        </div>
      </Card>

      <Card className="bg-[#1e1e24] border-[#2a2a32] p-4">
        <div className="flex flex-wrap items-center gap-4">
          <div className="flex-1 min-w-[200px]">
            <Input
              placeholder="Search documents..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="bg-[#16161a] border-[#2a2a32] text-white"
            />
          </div>
          <select
            value={categoryFilter}
            onChange={(e) => setCategoryFilter(e.target.value)}
            className="bg-[#16161a] border border-[#2a2a32] text-white rounded-lg px-3 py-2"
          >
            <option value="all">All Categories</option>
            <option value="guest_guide">Guide Partner Documents</option>
            <option value="onboarding">Onboarding</option>
            <option value="member">Member Resources</option>
            <option value="general">General</option>
          </select>
        </div>
      </Card>

      {loading ? (
        <div className="flex justify-center py-12">
          <Loader2 className="w-8 h-8 text-aqua animate-spin" />
        </div>
      ) : (
        <Card className="bg-[#1e1e24] border-[#2a2a32] overflow-hidden">
          {filteredDocs.length === 0 ? (
            <div className="p-8 text-center">
              <FileText className="w-12 h-12 text-gray-600 mx-auto mb-4" />
              <p className="text-gray-400">No documents found</p>
              <Button
                onClick={() => { resetForm(); setShowAddModal(true); }}
                className="mt-4 bg-aqua hover:bg-aqua-dark text-charcoal"
              >
                Add Your First Document
              </Button>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-[#2a2a32]">
                    <th className="text-left p-4 text-gray-400 font-medium">Document</th>
                    <th className="text-left p-4 text-gray-400 font-medium">Category</th>
                    <th className="text-left p-4 text-gray-400 font-medium">Type</th>
                    <th className="text-left p-4 text-gray-400 font-medium">Public</th>
                    <th className="text-left p-4 text-gray-400 font-medium">Created</th>
                    <th className="text-left p-4 text-gray-400 font-medium">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredDocs.map((doc) => {
                    const catStyle = categoryLabels[doc.category] || categoryLabels.general;
                    const typeInfo =
                      contentTypeLabels?.[doc?.content_type] ||
                      contentTypeLabels?.link ||
                      { label: 'Link', icon: FileText };
                    const TypeIcon = typeInfo.icon;
                    return (
                      <tr key={doc.document_id} className="border-b border-[#2a2a32] hover:bg-[#252530] transition-colors">
                        <td className="p-4">
                          <div>
                            <p className="text-white font-medium">{doc.name}</p>
                            {doc.description && (
                              <p className="text-gray-500 text-sm truncate max-w-xs">{doc.description}</p>
                            )}
                          </div>
                        </td>
                        <td className="p-4">
                          <Badge className={catStyle.color}>{catStyle.label}</Badge>
                        </td>
                        <td className="p-4">
                          <div className="flex items-center gap-2 text-gray-400">
                            <TypeIcon className="w-4 h-4" />
                            <span className="text-sm">{typeInfo.label}</span>
                          </div>
                        </td>
                        <td className="p-4">
                          {doc.is_public ? (
                            <Badge className="bg-green-500/10 text-green-400 border-green-500/30">Yes</Badge>
                          ) : (
                            <Badge className="bg-gray-500/10 text-gray-400 border-gray-500/30">No</Badge>
                          )}
                        </td>
                        <td className="p-4 text-gray-400 text-sm">
                          {doc.created_at ? new Date(doc.created_at).toLocaleDateString() : 'N/A'}
                        </td>
                        <td className="p-4">
                          <div className="flex items-center gap-2">
                            {getDocumentViewMeta(doc).hasViewableSource && !getDocumentViewMeta(doc).isMissingInternalFile && (
                              <button type="button" aria-label="View document"
                                onClick={() => openDocument(doc)}
                                className="p-2 text-gray-400 hover:text-aqua rounded"
                              >
                                <Eye className="w-4 h-4" />
                              </button>
                            )}
                            {getDocumentViewMeta(doc).isMissingInternalFile && (
                              <span
                                className="p-2 text-red-300"
                                title="Stored file is missing from server storage. Re-upload this document."
                              >
                                <Eye className="w-4 h-4" />
                              </span>
                            )}
                            <button
                              onClick={() => openEditModal(doc)}
                              className="p-2 text-gray-400 hover:text-blue-400 rounded"
                              title="Edit document"
                            >
                              <Edit className="w-4 h-4" />
                            </button>
                            <button
                              onClick={() => handleDeleteDocument(doc.document_id)}
                              className="p-2 text-gray-400 hover:text-red-400 rounded"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </Card>
      )}

      {showAddModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <Card className="bg-[#1e1e24] border-[#2a2a32] p-6 w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-white">Add Document</h3>
              <button onClick={() => setShowAddModal(false)} className="text-gray-400 hover:text-white">
                <X className="w-5 h-5" />
              </button>
            </div>
            {addFormJSX}
            <div className="flex gap-3 mt-6">
              <Button
                variant="outline"
                onClick={() => setShowAddModal(false)}
                className="flex-1 border-[#2a2a32] text-white hover:bg-[#252530]"
              >
                Cancel
              </Button>
              <Button
                onClick={handleAddDocument}
                disabled={saving}
                className="flex-1 bg-aqua hover:bg-aqua-dark text-charcoal"
              >
                {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                Add Document
              </Button>
            </div>
          </Card>
        </div>
      )}

      {showEditModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <Card className="bg-[#1e1e24] border-[#2a2a32] p-6 w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-white">Edit Document</h3>
              <button onClick={() => { setShowEditModal(false); setSelectedDoc(null); }} className="text-gray-400 hover:text-white">
                <X className="w-5 h-5" />
              </button>
            </div>
            {editFormJSX}
            <div className="flex gap-3 mt-6">
              <Button
                variant="outline"
                onClick={() => { setShowEditModal(false); setSelectedDoc(null); }}
                className="flex-1 border-[#2a2a32] text-white hover:bg-[#252530]"
              >
                Cancel
              </Button>
              <Button
                onClick={handleEditDocument}
                disabled={saving}
                className="flex-1 bg-aqua hover:bg-aqua-dark text-charcoal"
              >
                {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                {selectedFile ? 'Save + Replace File' : 'Save Changes'}
              </Button>
            </div>
          </Card>
        </div>
      )}
    </div>
  );
};

export default DocumentsTab;
